import React, { useEffect, useState, createRef } from 'react';
import Form from '@rjsf/core';
import { RJSFSchema, FormValidation, FieldValidation } from '@rjsf/utils';
import { Account, InventoryAsset, OutsideInventorySchema, Product } from '../../../types';
import { JSONSchemaForm } from '../JSONSchemaForm';
import { outsideInventoryWidgets } from './outsideInventoryWidgets';
import { getProductSubject } from '../../../store/Product';
import { getOutsideInventorySchemaSubject } from '../../../store/OutsideInventorySchema';
import { getAccounts, getAccountScope } from '../../../store/User';
import { defaultOutsideInventoryForm, updateFormProduct } from '../../../store/OutsideInventory';
import { ProductSelectionForm } from '../ProductSelectionForm';
import { AccountSelectionForm } from '../UserCreation/AccountSelectionForm';

export interface OutsideInventoryFormComponentProps {
  formId: string;
  updateForm: (id: string, formData: InventoryAsset, dirty?: boolean, valid?: boolean) => void;
  formData: InventoryAsset;
  validateForm: boolean;
}

// Pairs of (value, unit) speed fields that must have a unit when the value is
// populated. Enforced as a customValidate because the JSON Schema if/then
// equivalent crashed RJSF v5; see the [UPDEV-4326] API commit history.
const SPEED_UNIT_REQUIRED_PAIRS: ReadonlyArray<[string, string]> = [
  ['speedValue', 'speedUnit'],
  ['portSpeedValue', 'portSpeedUnit'],
  ['accessSpeedValue', 'accessSpeedUnit'],
  ['upSpeedValue', 'upSpeedUnit'],
  ['downSpeedValue', 'downSpeedUnit']
];

// RJSF's createErrorHandler only seeds entries for keys present in formData.
// If a unit field has never been touched it won't be in formData, so
// `errors[unitKey]` is undefined and `addError` silently no-ops. Build a
// FieldValidation-shaped entry on demand so the error is recorded.
const ensureFieldValidation = (errors: FormValidation, key: string): FieldValidation => {
  const existing = errors[key];
  if (existing && typeof existing.addError === 'function') return existing;
  const fieldErrors: string[] = [];
  const created = {
    __errors: fieldErrors,
    addError(message: string) {
      fieldErrors.push(message);
    }
  } as unknown as FieldValidation;
  (errors as Record<string, FieldValidation>)[key] = created;
  return created;
};

export const validateSpeedUnits = <T extends Record<string, unknown>>(
  formData: T,
  errors: FormValidation
): FormValidation => {
  SPEED_UNIT_REQUIRED_PAIRS.forEach(([valueKey, unitKey]) => {
    const value = formData?.[valueKey];
    const unit = formData?.[unitKey];
    const hasValue = typeof value === 'string' ? value.trim() !== '' : value != null;
    const hasUnit = typeof unit === 'string' ? unit.trim() !== '' : unit != null;
    if (hasValue && !hasUnit) {
      ensureFieldValidation(errors, unitKey).addError('is required when a value is provided');
    }
  });
  return errors;
};

export const OutsideInventoryForm = ({
  formId,
  formData,
  validateForm,
  updateForm
}: OutsideInventoryFormComponentProps) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [product, setProduct] = useState<Product | null>(null);
  const [allSchemas, setAllSchemas] = useState<OutsideInventorySchema[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(
    getAccountScope()?.length === 1 ? getAccountScope()?.[0] : undefined
  );

  const [schema, setSchema] = useState<OutsideInventorySchema | null>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formRef = createRef<Form<any, RJSFSchema, any>>();

  useEffect(() => {
    const productSub = getProductSubject().subscribe((products) => setAllProducts(products));
    const schemasSub = getOutsideInventorySchemaSubject().subscribe((schemas) => setAllSchemas(schemas));

    return () => {
      if (productSub) productSub.unsubscribe();
      if (schemasSub) schemasSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (product) {
      const matchedSchema = updateFormProduct(formId, product, allSchemas, selectedAccount?.id);
      if (matchedSchema) setSchema(matchedSchema);
      else updateForm(formId, defaultOutsideInventoryForm().formData, false);
    }
  }, [product, selectedAccount]);

  const handleInputChange = (e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setProductSearchQuery(value || '');
  };

  const handleProductSelectionChange = (e: React.SyntheticEvent<Element, Event>, value: Product | null) => {
    setProduct(value);
  };

  const handleChange = (form: InventoryAsset | null) => {
    const update: InventoryAsset | null = { ...formData, ...form };
    const formValid = formRef.current?.validateForm();
    updateForm(formId, update, true, formValid);
  };

  const customValidate = (data: InventoryAsset, errors: FormValidation): FormValidation =>
    validateSpeedUnits(data as unknown as Record<string, unknown>, errors);

  return (
    <div className="w-full border-b-2">
      <div className="flex space-x-4">
        <AccountSelectionForm
          accounts={getAccounts() || []}
          setSelectedAccount={(v) => setSelectedAccount(v)}
          selectedAccount={selectedAccount || null}
          styles="mb-7 w-72 mt-4"
          showLabel={true}
          inputStyles="h-[1.75rem]"
          size="medium"
        />
        <ProductSelectionForm
          disabled={!selectedAccount}
          product={product}
          allProducts={allProducts}
          productSearchQuery={productSearchQuery}
          handleInputChange={handleInputChange}
          handleProductSelectionChange={handleProductSelectionChange}
          classNames="mb-7 w-72 mt-4"
        />
      </div>
      {product && schema && (
        <JSONSchemaForm
          formKey={formData.id}
          schema={schema.schema.validation_schema}
          uiSchema={schema.schema.ui_schema}
          formData={formData}
          onChangeHandler={handleChange}
          rjsfProps={{
            widgets: outsideInventoryWidgets,
            idPrefix: 'outside_inventory_form',
            idSeparator: `_${formId}_`,
            showErrorList: false,
            liveValidate: validateForm,
            customValidate: customValidate,
            ref: formRef
          }}
        />
      )}
    </div>
  );
};
